import React from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import SEO from './SEO';
import SEOErrorBoundary from './SEOErrorBoundary';
import useStructuredData from '../hooks/useStructuredData';
import { useLanguageUrl } from '../utils/languageUtils';

const LandingPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated } = useAuth();
  const { getOrganizationData, getWebSiteData, getServiceData } = useStructuredData();
  const getLanguageUrl = useLanguageUrl();

  // Combine multiple structured data objects
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      getOrganizationData(),
      getWebSiteData(),
      getServiceData()
    ]
  };

  return (
    <>
      <SEOErrorBoundary
        fallbackTitle={t('landing.title')}
        fallbackDescription={t('landing.description')}
      >
        <SEO 
          title={t('landing.title')}
          description={t('landing.description')}
          structuredData={structuredData}
          hreflangEnabled={true}
        />
      </SEOErrorBoundary>
      <Container maxWidth="lg">
      <Box sx={{ py: { xs: 4, md: 8 } }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant={isMobile ? 'h3' : 'h1'}
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 3,
              color: 'primary.main',
              fontSize: { xs: '2.5rem', md: '3.5rem' },
            }}
          >
            {t('landing.title')}
          </Typography>
          
          <Typography
            variant="h6"
            sx={{
              mb: 4,
              color: 'text.secondary',
              maxWidth: '600px',
              mx: 'auto',
              lineHeight: 1.6,
              fontSize: { xs: '1.1rem', md: '1.25rem' },
            }}
          >
            {t('landing.description')}
          </Typography>

          {!isAuthenticated && (
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                component={Link}
                to={getLanguageUrl('register')}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  minWidth: 160,
                }}
              >
                {t('landing.registerButton')}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                component={Link}
                to={getLanguageUrl('howItWorks')}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  minWidth: 160,
                }}
              >
                {t('nav.howItWorks')}
              </Button>
            </Box>
          )}
        </Box>

      </Box>
    </Container>
    </>
  );
};

export default LandingPage;